// pms-adapters / supabase / frontDesk — the team's « Arrivées » / « Départs »
// list on staff.* (operator 2026-09-30).
//
// Two narrow RPCs behind `pms.frontdesk.read`, which an employee holds (the
// desk's `pms.payment.read` they do not): rooms, nights, what is left to
// collect — no names, no payment lines. The RPCs refuse a past day themselves;
// the screen never asks for one.
//
// Read-only, so no L2 gate. Interim source, like the desk sheets: the Vega
// mirror. At go-live the adapter changes, not the screen.

import type {
  FrontDeskArrival,
  FrontDeskBriefing,
  FrontDeskDeparture,
  Instant,
  IsoDate,
  Result,
} from '@lemontaigu/pms-core';
import { fromList, type SupabaseClient } from './support.js';

export interface FrontDeskArrivalRow {
  rooms: string[] | null;
  nights: number;
  due_cents: number | null;
  expected_time: string | null;
  read_at: string | null;
}

export interface FrontDeskDepartureRow {
  rooms: string[] | null;
  nights: number;
  due_cents: number;
  read_at: string | null;
}

export const toFrontDeskArrival = (r: FrontDeskArrivalRow): FrontDeskArrival => ({
  rooms: r.rooms ?? [],
  nights: Number(r.nights),
  dueCents: r.due_cents === null ? null : Number(r.due_cents),
  expectedTime: r.expected_time,
  readAt: r.read_at as Instant | null,
});

export const toFrontDeskDeparture = (r: FrontDeskDepartureRow): FrontDeskDeparture => ({
  rooms: r.rooms ?? [],
  nights: Number(r.nights),
  dueCents: Number(r.due_cents),
  readAt: r.read_at as Instant | null,
});

export class SupabaseVegaFrontDeskBriefing implements FrontDeskBriefing {
  constructor(private readonly db: SupabaseClient) {}

  async notYetArrived(date: IsoDate): Promise<Result<FrontDeskArrival[]>> {
    const r = await this.db.rpc('pms_staff_arrivals', { p_date: date });
    return fromList<FrontDeskArrivalRow, FrontDeskArrival>(
      r as { data: FrontDeskArrivalRow[] | null; error: typeof r.error },
      toFrontDeskArrival,
    );
  }

  async departuresToCollect(date: IsoDate): Promise<Result<FrontDeskDeparture[]>> {
    const r = await this.db.rpc('pms_staff_departures', { p_date: date });
    return fromList<FrontDeskDepartureRow, FrontDeskDeparture>(
      r as { data: FrontDeskDepartureRow[] | null; error: typeof r.error },
      toFrontDeskDeparture,
    );
  }
}
